import React, { FC } from 'react';
import Image, { ImageLoaderProps } from 'next/image';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import { theme } from '@care/material-ui-theme';
import { Typography } from '@care/react-component-lib';
import CompanySearchForm from '@/components/pages/MemberEnrollment/Welcome/components/CompanySearchForm';
import { useEventLogger } from '@/utilities/analyticsHelper';
import { WELCOME_IMAGES } from '../../images';

const Hero: FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const xxs = '@media (max-height: 900px) and (max-width: 600px)'; // for hiding hero image on very small screens
  const logger = useEventLogger({
    user_flow: 'Direct to Enrollment',
  });

  const floralClusterImg = {
    src: isMobile ? WELCOME_IMAGES.FLORAL_MW : WELCOME_IMAGES.FLORAL_LEFT,
    alt: 'cluster of flowers',
    className: isMobile ? 'flowerMW' : 'flowerLeft',
  };
  const floralImg = {
    src: WELCOME_IMAGES.FLORAL_RIGHT,
    alt: 'flower',
    className: 'flowerRight',
  };
  const momAndChildImg = {
    src: isMobile ? WELCOME_IMAGES.MOM_AND_CHILD_MW : WELCOME_IMAGES.MOM_AND_CHILD_DT,
    alt: 'mother reading with child',
  };
  const headerText = `Join to find care when and where you need it`;
  const subHeaderText = `Get access to your free, employer-sponsored account today`;

  const heroLayout = isMobile
    ? `'image'
       'formWrapper'
  `
    : `'formWrapper image'`;

  const formWrapperLayout = `
    'header'
    'subHeader'
    'form'`;

  const classes = makeStyles(() => ({
    hero: {
      position: 'relative',
      display: 'grid',
      gridTemplateAreas: heroLayout,
      gridAutoColumns: isMobile ? '100%' : '1fr auto',
      justifyContent: 'space-between',
      paddingLeft: isMobile ? '24px' : '152px',
      backgroundColor: theme.palette.care.grey[50],
      overflow: isMobile ? 'initial' : 'hidden',
      zIndex: 0,
      '& .formWrapper': {
        display: 'grid',
        gridTemplateAreas: formWrapperLayout,
        gridTemplateRows: 'auto auto 1fr',
        justifyContent: isLargeScreen ? 'center' : 'initial',
        '& .header': {
          gridArea: 'header',
          marginTop: isMobile ? 0 : '100px',
          marginBottom: '16px',
          [xxs]: {
            marginTop: '80px',
          },
        },
        '& .subHeader': {
          gridArea: 'subHeader',
          alignSelf: 'center',
        },
        '& .form': {
          gridArea: 'form',
          marginBottom: isMobile ? '32px' : '90px',
        },
      },
      '& .heroImageWrapper': {
        gridArea: 'image',
        position: 'relative',
        minHeight: isMobile ? 0 : '548px',
        height: isMobile ? '450px' : 'initial',
        maxHeight: isMobile ? 'initial' : '650px',
        width: isMobile ? '574px' : '640px',
        margin: 0,
        pointerEvents: 'none',
        [xxs]: {
          height: 0,
        },
        [theme.breakpoints.up('lg')]: {
          width: '40vw',
        },
      },
      '& .flowerRight': {
        position: 'absolute',
        left: isLargeScreen ? 'unset' : '10%',
        right: isLargeScreen ? 'calc(40vw - 180px)' : 'unset',
        height: '82px',
        width: '140px',
      },
      '& .flowerLeft': {
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: '200px',
        width: '139px',
      },
      '& .flowerMW': {
        position: 'absolute',
        top: 0,
        right: 0,
        height: '139px',
        width: '189px',
      },
      '& img': {
        zIndex: -1,
      },
    },
  }))();

  // This is a temporary optimization. By appending the `?im=` parameter, we are getting an optimized image from Akamai.
  // Images optimized in this way are cached on edge servers only after first fetch from each server and stay cached for at least 30 days.
  // TODO - replace with standard loader once we establish one.
  const imageLoader = ({ src }: ImageLoaderProps): string => `${src}?im=`;

  const floralClusterImage = (
    <Image
      priority
      src={floralClusterImg.src}
      alt={floralClusterImg.alt}
      layout="fill"
      objectFit="contain"
      loader={imageLoader}
      sizes="50vw"
    />
  );

  const heroImage = (
    <Image
      className="heroImage"
      priority
      src={momAndChildImg.src}
      alt={momAndChildImg.alt}
      layout="fill"
      objectFit="cover"
      objectPosition={isMobile ? 'top' : 'center'}
      loader={imageLoader}
      sizes="50vw"
    />
  );

  const flowerRightImage = !isMobile && (
    <div className={floralImg.className}>
      <Image
        priority
        src={floralImg.src}
        alt={floralImg.alt}
        layout="fill"
        objectFit="contain"
        loader={imageLoader}
        sizes="50vw"
      />
    </div>
  );

  const renderForm = () => (
    <div className="formWrapper">
      <Typography careVariant="display3" className="header">
        {headerText}
      </Typography>
      <Typography variant="body1" className="subHeader">
        {subHeaderText}
      </Typography>
      <div className="form">
        <CompanySearchForm logger={logger} />
      </div>
    </div>
  );

  return (
    <section className={classes.hero}>
      {renderForm()}
      <figure className="heroImageWrapper">
        {heroImage}
        {flowerRightImage}
      </figure>
      <div className={floralClusterImg.className}>{floralClusterImage}</div>
    </section>
  );
};

export default Hero;
